import { useState, useEffect, useCallback } from 'react'
import { BarChart3, BookOpen, Check, Clock, Fingerprint } from 'lucide-react'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts'
import { api } from '../services/api'
import { formatCurrency, getMonthLabel, getMonthShort } from '../utils/format'

export default function History() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [months, setMonths] = useState(6)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const result = await api.getHistory(months)
      setData(result)
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }, [months])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    const onEvents = (e) => {
      const evs = e.detail || []
      if (evs.length > 0) {
        fetchData()
      }
    }
    window.addEventListener('fb:events', onEvents)
    return () => window.removeEventListener('fb:events', onEvents)
  }, [fetchData])

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-primary-700 border-t-transparent rounded-full animate-spin" /></div>
  }

  if (!data) return null

  // Monthly totals chart
  const monthlyData = data.monthlyTotals.map(m => ({
    label: getMonthShort(m.month),
    total: m.total,
    count: m.count,
  }))

  // Budget vs actual
  const budgetData = data.budgetVsActual.map(m => ({
    label: getMonthShort(m.month),
    budget: m.budget_total || 0,
    reel: m.expense_total || 0,
  }))

  // Top categories
  const topCats = data.topCategories.slice(0, 5)

  // Monthly by category (stacked data preparation)
  const monthsSet = [...new Set(data.monthlyByCategory.map(m => m.month))].sort()
  const categoriesSet = [...new Set(data.monthlyByCategory.map(m => m.category_name))]

  const stackedData = monthsSet.map(m => {
    const entry = { label: getMonthShort(m) }
    for (const cat of categoriesSet) {
      const found = data.monthlyByCategory.find(d => d.month === m && d.category_name === cat)
      if (found) entry[cat] = found.total
    }
    return entry
  })

  const avgMonthly = data.monthlyTotals.length > 0
    ? data.monthlyTotals.reduce((sum, m) => sum + m.total, 0) / data.monthlyTotals.length
    : 0

  const totalAllTime = data.monthlyTotals.reduce((sum, m) => sum + m.total, 0)

  // Grouper les paiements Cahier par mois
  const cahierByMonth = (data.cahierPayments || []).reduce((acc, p) => {
    if (!acc[p.month]) acc[p.month] = []
    acc[p.month].push(p)
    return acc
  }, {})
  const cahierMonths = Object.keys(cahierByMonth).sort().reverse()

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2"><BarChart3 size={20} /> Historique</h2>
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="input py-1.5 w-auto text-sm">
          <option value={3}>3 mois</option>
          <option value={6}>6 mois</option>
          <option value={12}>12 mois</option>
        </select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card text-center">
          <p className="text-sm text-slate-500">Total période</p>
          <p className="text-xl font-bold">{formatCurrency(totalAllTime)}</p>
        </div>
        <div className="card text-center">
          <p className="text-sm text-slate-500">Moyenne / mois</p>
          <p className="text-xl font-bold text-primary-700">{formatCurrency(avgMonthly)}</p>
        </div>
      </div>

      {/* Monthly trend */}
      {monthlyData.length > 0 && (
        <div className="card">
          <h3 className="font-semibold mb-3">Dépenses mensuelles</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Line type="monotone" dataKey="total" stroke="#0f766e" strokeWidth={3} dot={{ r: 4, fill: '#0f766e' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Budget vs Actual */}
      {budgetData.length > 0 && (
        <div className="card">
          <h3 className="font-semibold mb-3">Budget vs Réel</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={budgetData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="budget" fill="#94a3b8" radius={[4, 4, 0, 0]} name="Budget" />
              <Bar dataKey="reel" fill="#0f766e" radius={[4, 4, 0, 0]} name="Réel" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top categories */}
      {topCats.length > 0 && (
        <div className="card">
          <h3 className="font-semibold mb-3">Top catégories (période)</h3>
          <div className="space-y-2">
            {topCats.map((cat, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xl">{cat.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium">{cat.name}</span>
                    <span className="text-slate-500">{formatCurrency(cat.total)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(cat.total / topCats[0].total) * 100}%`, background: cat.color }} />
                  </div>
                </div>
                {!cat.is_essential && <span className="badge bg-amber-50 text-amber-600 text-[10px]">Non ess.</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly breakdown table */}
      {data.monthlyTotals.length > 0 && (
        <div className="card">
          <h3 className="font-semibold mb-3">Détail par mois</h3>
          <div className="space-y-2">
            {data.monthlyTotals.map((m, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                <span className="capitalize font-medium">{getMonthLabel(m.month)}</span>
                <div className="text-right">
                  <p className="font-semibold">{formatCurrency(m.total)}</p>
                  <p className="text-xs text-slate-400">{m.count} dépense(s)</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Cahier de suivi — paiements charges récurrentes */}
      {cahierMonths.length > 0 && (
        <div className="card">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <BookOpen size={18} className="text-primary-600" /> Cahier de suivi
          </h3>
          <div className="space-y-4">
            {cahierMonths.map(month => {
              const items = cahierByMonth[month]
              const monthTotal = items.reduce((s, p) => s + (p.amount || 0), 0)
              return (
                <div key={month}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-600 capitalize">{getMonthLabel(month)}</span>
                    {monthTotal > 0 && (
                      <span className="text-xs text-slate-400">{formatCurrency(monthTotal)}</span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {items.map((p, i) => {
                      const isFullySigned = p.has_biometric && p.sig_count >= 2
                      const isPartial    = p.has_biometric && p.sig_count === 1
                      const isSimple     = !p.has_biometric
                      return (
                        <div key={i} className="flex items-center gap-2.5 py-1.5 border-b border-slate-50 last:border-0">
                          {/* Icône statut */}
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                            isFullySigned ? 'bg-green-100 text-green-600' :
                            isPartial     ? 'bg-amber-100 text-amber-600' :
                            isSimple      ? 'bg-primary-100 text-primary-600' :
                                           'bg-slate-100 text-slate-400'
                          }`}>
                            {isFullySigned ? <Check size={12} /> :
                             isPartial     ? <span className="text-[9px] font-bold">1/2</span> :
                             isSimple      ? <Check size={12} /> :
                                           <Clock size={10} />}
                          </span>
                          {/* Catégorie */}
                          <span className="flex-1 text-sm">
                            {p.category_icon && <span className="mr-1">{p.category_icon}</span>}
                            {p.category_name}
                          </span>
                          {/* Type */}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                            isFullySigned ? 'bg-green-50 text-green-600' :
                            isPartial     ? 'bg-amber-50 text-amber-600' :
                            'bg-primary-50 text-primary-600'
                          }`}>
                            {isFullySigned ? <span className="flex items-center gap-0.5"><Fingerprint size={9} /> 2/2</span> :
                             isPartial     ? <span className="flex items-center gap-0.5"><Fingerprint size={9} /> 1/2</span> :
                             'Agence'}
                          </span>
                          {/* Montant */}
                          {p.amount > 0 && (
                            <span className="text-sm font-medium text-slate-700 tabular-nums">{formatCurrency(p.amount)}</span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
